import { routes } from "@constants/routes";
import { LocationBoldIcon, useFetchFile, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import { AuthAccountJobPositionAssignmentStatusChoices, GetJobPositionAssignmentsQuery } from "@graphql/generated";
import { formatString } from "@modules/Resume/utils/formatString";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const Dot = ({ className }: { className?: string }) => (
  <div className={`w-1 h-1 rounded-full bg-zinc-500 mx-2 ${className}`} />
);

interface IJobCard {
  jobPosition?: NonNullable<
    NonNullable<
      NonNullable<NonNullable<NonNullable<GetJobPositionAssignmentsQuery>["me"]>["jobPositionAssignments"]>["edges"]
    >["0"]
  >["node"];
  status?: AuthAccountJobPositionAssignmentStatusChoices;
  isDisabled?: boolean;
}

const JobCard = ({ jobPosition, status, isDisabled }: IJobCard) => {
  const navigate = useNavigate();

  const companyLogoFile = jobPosition?.jobPosition?.organization?.logo?.file;
  const { data: companyLogo } = useFetchFile({ urlPath: companyLogoFile, userType: UserTypeEnum.USER });
  const companyName = jobPosition?.jobPosition?.organization?.name;

  const title = jobPosition?.jobPosition?.title;
  const contractType = jobPosition?.jobPosition?.contractType;
  const salary = jobPosition?.jobPosition?.salaryRange;
  const cities = jobPosition?.jobPosition?.cities.edges.map((item) => item?.node?.name).join(", ");
  const postedDate = jobPosition?.jobPosition?.startAt
    ? dayjs(jobPosition?.jobPosition?.startAt).format("YYYY/MM/DD")
    : "";
  const description = jobPosition?.jobPosition?.description;
  const locationType = jobPosition?.jobPosition?.locationType;

  const navigateToViewPage = () => {
    if (!jobPosition?.id || isDisabled) return;

    navigate(routes.jobSuggestions.view.replace(":id", jobPosition?.id));
  };

  return (
    <div
      onClick={navigateToViewPage}
      className={`${!isDisabled && "cursor-pointer"} hover:bg-zinc-100 border-1 border-zinc-200 p-5 rounded-xl ${(status || isDisabled) && "bg-zinc-50"} overflow-hidden`}
    >
      <div className="flex gap-3 flex-col">
        <div className="md:flex justify-between items-center">
          <div className="md:flex items-center">
            <div className="flex  gap-4 items-center mb-4 md:mb-0">
              {companyLogo ? (
                <img
                  className="w-10 h-10 md:w-14 md:h-14 rounded-xl object-cover"
                  src={companyLogo}
                  alt={companyLogo}
                />
              ) : (
                <div className="bg-slate-300 w-10 h-10 md:w-14 md:h-14 rounded-xl" />
              )}
              <div className="flex-col md:flex-row">
                <p className="font-semibold text-sm md:text-base">{title}</p>
                <p className="text-xs md:hidden font-medium text-zinc-500">{companyName}</p>

                <div className=" flex-row hidden md:flex items-center">
                  <p className="text-xs font-medium text-zinc-500">{companyName}</p>
                  {contractType && (
                    <>
                      <Dot className="hidden md:block" />
                      <p className="text-xs text-[#C4841D] bg-[#FDEDD3] py-1 px-2 rounded-lg">
                        {formatString(contractType)}
                      </p>
                    </>
                  )}
                  {locationType && (
                    <>
                      <Dot className="hidden md:block" />
                      <p className="text-xs text-blue-500 bg-blue-100 py-1 px-2 rounded-lg">
                        {formatString(locationType)}
                      </p>
                    </>
                  )}
                  {salary && (salary[0] || salary[1]) && (
                    <>
                      <Dot />
                      <p className="text-xs font-medium text-zinc-500">
                        {salary[0] && `${salary[0]}$`} {salary[1] && `- ${salary[1]}$`}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className=" flex-row flex md:hidden gap-5 items-center">
            {contractType && (
              <>
                <Dot className="hidden md:block" />
                <p className="text-xs text-[#C4841D] bg-[#FDEDD3] py-1 px-2 rounded-lg">{formatString(contractType)}</p>
              </>
            )}
            {locationType && (
              <>
                <Dot className="hidden md:block" />
                <p className="text-xs text-blue-500 bg-blue-100 py-1 px-2 rounded-lg">{formatString(locationType)}</p>
              </>
            )}
          </div>
          {salary && (
            <div className=" flex-row flex md:hidden gap-5 items-center mt-3">
              <p className="text-xs font-medium text-zinc-500">
                {salary[0] && `${salary[0]}$`} {salary[1] && `- ${salary[1]}$`}
              </p>
            </div>
          )}
          <div className="flex items-center mt-3 md:mt-0 justify-between md:justify-start md:flex-col md:items-end">
            <div className="flex items-center">
              {cities && (
                <>
                  <LocationBoldIcon className="w-4 h-4 md:w-6 md:h-6" />
                  <p className="text-xs md:text-sm">{cities}</p>
                </>
              )}
            </div>
            {postedDate && <p className="text-zinc-500 text-xs md:text-sm">Posted at {postedDate} </p>}{" "}
          </div>
        </div>
        {description && <p className="text-xs text-zinc-600 leading-6 line-clamp-3">{description}</p>}{" "}
        {status && (
          <div className="flex flex-col md:flex-row items-center justify-between -mt-3">
            <div></div>
            {status === AuthAccountJobPositionAssignmentStatusChoices.ACCEPTED && (
              <p className="md:text-3xl  font-bold md:font-semibold opacity-20 text-green-600">Accept send resume</p>
            )}
            {status === AuthAccountJobPositionAssignmentStatusChoices.REJECTED && (
              <p className=" md:text-3xl font-bold md:font-semibold opacity-20 text-primary-600">You have rejected</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;
